<template>
  <standard-container>
    <template v-slot:title>
      <v-btn
        class="ma-2"
        icon
        :has-footer="false"
        @click="prevMonth"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <div class="d-flex align-center">
        <v-select
          v-model="type"
          :items="translatedCalendarTypes"
          dense
          outlined
          hide-details
          class="ma-2"
          :label="$t('akito.sub-type')"
          @change="scrollToNow"
        />
        <v-btn
          color="warning"
          class="ma-2"
          outlined
          @click="setToday"
        >
          {{ $t("calendar.weather.today") }}
        </v-btn>
      </div>

      <v-btn
        class="ma-2"
        icon
        @click="nextMonth"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </template>
    <template v-slot:content>
      <v-calendar
        ref="calendar"
        v-model="focus"
        class="calendar-content"
        :events="events"
        :event-color="getEventColor"
        :type="type"
        :now="today"
        :value="today"
        :weekdays="[1, 2, 3, 4, 5, 6, 0]"
      />
    </template>
  </standard-container>
</template>

<script>
import standardContainer from "../Common/StandardContainer";

export default {
  components: {
    standardContainer,
  },
  props: {
    schedule: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    today: new Date().toISOString().substr(0, 10),
    focus: new Date(),
    type: "month",
    events: [],
    calendarTypes: ["day", "week", "month"],
    projectColors: {},
  }),
  computed: {
    translatedCalendarTypes() {
      return this.calendarTypes.map((type) => ({
        value: type,
        text: this.$t(`calendar.types.${type}`),
      }));
    },
  },
  watch: {
    schedule: {
      handler(val) {
        if (val) {
          this.getEvents();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.getEvents();
  },
  methods: {
    getEvents() {
      const today = new Date();
      this.events = [];

      this.schedule.forEach((schedule) => {
        // Project name
        const projectName = this.cleanName(schedule.projectData.name);
        // Define color if not already defined
        if (!this.projectColors[projectName]) {
          this.projectColors[projectName] = this.getRandomColor();
        }

        if (schedule.permanent) {
          // Define start date on first day of current month
          const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          // Define end date in two month later
          const endDate = new Date(today.getFullYear(), today.getMonth() + 2, today.getDate());

          // Push object in events to make it appear in calendar
          this.events.push({
            name: projectName,
            start: startDate,
            end: endDate,
            color: this.projectColors[projectName],
            timed: false,
          });
        } else {
          schedule.days.forEach((day) => {
            if (day.active) {
              const dayIndex = this.getDayIndex(day.day);

              // Define start date according to parameters
              let sDate = new Date(today);
              if (schedule.dateFrom != null) {
                sDate = new Date(schedule.dateFrom);
              }
              // Define end date according to parameters
              let eDate = new Date(today.getFullYear(), today.getMonth() + 2, today.getDate());
              if (schedule.dateTo != null) {
                eDate = new Date(schedule.dateTo);
              }

              // Make one entity by day
              while (sDate <= eDate) {
                if (sDate.getDay() === dayIndex) {
                  const eventStart = new Date(sDate);
                  const eventEnd = new Date(sDate);

                  // By default start each day at 0h00
                  eventStart.setHours(0, 0, 0);
                  if (schedule.timeFrom != null) {
                    const [startHour, startMinute] = schedule.timeFrom.split(":").map(Number);
                    eventStart.setHours(startHour, startMinute, 0);
                  }

                  // By default end each day at 23h59
                  eventEnd.setHours(23, 59, 59);
                  if (schedule.timeTo != null) {
                    const [endHour, endMinute] = schedule.timeTo.split(":").map(Number);
                    eventEnd.setHours(endHour, endMinute, 59);
                  }

                  // If time from is over time to add one day to end
                  if (schedule.timeFrom > schedule.timeTo) {
                    eventEnd.setDate(eventEnd.getDate() + 1);
                  }

                  // If permanent or without hours defined we set timed to false
                  let isTimed = true;
                  if (schedule.timeFrom == null && schedule.timeTo == null) {
                    isTimed = false;
                  }

                  // Push object in events to make it appear in calendar
                  this.events.push({
                    name: projectName,
                    start: eventStart,
                    end: eventEnd,
                    color: this.projectColors[projectName],
                    timed: isTimed,
                  });
                }

                sDate.setDate(sDate.getDate() + 1);
              }
            }
          });
        }
      });
    },
    getDayIndex(day) {
      const days = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };
      return days[day.toLowerCase()];
    },
    cleanName(name) {
      return name.startsWith("_") ? name.substring(1) : name;
    },
    getEventColor(event) {
      return event.color;
    },
    getRandomColor() {
      const colors = [
        "success",
        "info",
        "error",
        "warning",
        "primary",
        "secondary",
        "accent",
        "purple",
        "pink",
        "indigo",
        "teal",
        "cyan",
        "green",
        "lime",
        "amber",
        "deep-orange",
        "blue-grey",
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },
    setToday() {
      this.focus = new Date();
      this.$refs.calendar.scrollToTime("07:30");
    },
    prevMonth() {
      this.$refs.calendar.prev();
      this.scrollToNow();
    },
    nextMonth() {
      this.$refs.calendar.next();
      this.scrollToNow();
    },
    scrollToNow() {
      // Can't make it without it
      this.$nextTick(() => {
        this.$refs.calendar.scrollToTime("07:30");
      });
    },
  },
};
</script>

<style scoped>
.calendar-container {
  overflow: auto;
}
</style>
